import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, MapPin, ArrowLeft, Notepad, CaretRight, Camera, VideoCamera, LinkBreak } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { portalClient, fmtDate, fmtTime } from "@/components/service/serviceApi";
import { LiveSearchBar } from "@/components/service/LiveSearchBar";
import { useLiveSearch } from "@/components/service/useLiveSearch";
import { RecordModal } from "@/components/service/RecordModal";
import { PhotoViewer } from "@/components/service/PhotoViewer";
import { VideoViewer } from "@/components/service/VideoViewer";
import { MediaGallery, Section } from "@/components/service/DetailParts";
import { StatusBadge } from "@/components/service/RecordsPane";

const SORTS = {
  newest: (a, b) => `${b.service_date}${b.service_time}`.localeCompare(`${a.service_date}${a.service_time}`),
  oldest: (a, b) => `${a.service_date}${a.service_time}`.localeCompare(`${b.service_date}${b.service_time}`),
  title: (a, b) => a.title.localeCompare(b.title),
  category: (a, b) => (a.category_name || "").localeCompare(b.category_name || ""),
  unit: (a, b) => (a.unit || "").localeCompare(b.unit || "", undefined, { numeric: true }),
};

const useNoIndex = () => useEffect(() => {
  const metas = [["robots", "noindex, nofollow"], ["referrer", "no-referrer"]].map(([n, c]) => {
    const m = document.createElement("meta"); m.name = n; m.content = c; document.head.appendChild(m); return m;
  });
  return () => metas.forEach((m) => m.remove());
}, []);

function RecordCard({ r, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(r)} className="w-full text-left bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-3 active:scale-[0.99] transition-transform" data-testid={`portal-record-${r.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2"><span className="font-semibold text-slate-900 truncate">{r.title}</span><StatusBadge status={r.status} /></div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">{fmtDate(r.service_date)}{r.service_time ? ` · ${fmtTime(r.service_time)}` : ""} · {r.category_name}{r.unit ? ` · ${r.unit}` : ""}</div>
        <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2">
          {r.service_person_name && <span>{r.service_person_name}</span>}
          {r.photo_count > 0 && <span className="inline-flex items-center gap-0.5"><Camera size={10} />{r.photo_count}</span>}
          {r.video_count > 0 && <span className="inline-flex items-center gap-0.5"><VideoCamera size={10} />{r.video_count}</span>}
        </div>
      </div>
      <CaretRight size={16} className="text-slate-300" />
    </button>
  );
}

function PortalDetail({ detail, onBack, onOpenMedia }) {
  const { record: r, media } = detail;
  const rows = [["Service date", `${fmtDate(r.service_date)}${r.service_time ? ` · ${fmtTime(r.service_time)}` : ""}`], ["Category", r.category_name], ["Unit", r.unit], ["Service person", r.service_person_name], ["Brand", r.equipment_brand], ["Model number", r.equipment_model], ["Serial number", r.equipment_serial]];
  return (
    <div className="space-y-5" data-testid="portal-detail">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm font-semibold text-[#327de3]" data-testid="portal-back-btn"><ArrowLeft size={14} /> All service logs</button>
      <div>
        <div className="flex items-center gap-2 flex-wrap"><h2 className="text-xl font-bold text-slate-900" data-testid="portal-detail-title">{r.title}</h2><StatusBadge status={r.status} /></div>
        <div className="text-[11px] text-slate-400 mt-0.5">Read-only · logged {r.source === "qr_portal" ? "via QR portal" : "in Strata"}</div>
      </div>
      <div className="grid grid-cols-2 gap-3 bg-white rounded-xl border border-slate-200 p-4">
        {rows.map(([l, v]) => <div key={l}><div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{l}</div><div className="text-sm font-medium text-slate-800">{v || "—"}</div></div>)}
      </div>
      <Section icon={<Notepad size={14} />} title="Service notes">
        <div className="text-sm text-slate-700 whitespace-pre-wrap bg-white rounded-xl border border-slate-200 px-4 py-3" data-testid="portal-detail-notes">{r.notes || <span className="text-slate-400">No notes.</span>}</div>
      </Section>
      <MediaGallery media={media} onOpen={onOpenMedia} />
    </div>
  );
}

class PortalErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f7fb] p-6" data-testid="portal-error">
        <div className="text-center max-w-xs space-y-3">
          <div className="text-lg font-bold text-slate-800">Something went wrong</div>
          <div className="text-sm text-slate-500">Your saved entries are safe. Tap below to reload the service logs.</div>
          <Button onClick={() => { this.setState({ error: null }); window.location.reload(); }} className="bg-[#327de3] text-white" data-testid="portal-error-reload">Reload</Button>
        </div>
      </div>
    );
  }
}

export default function ServicePortalPage() {
  return <PortalErrorBoundary><ServicePortalInner /></PortalErrorBoundary>;
}

function ServicePortalInner() {
  const { token } = useParams();
  const client = useMemo(() => portalClient(token), [token]);
  const [info, setInfo] = useState(null);
  const [failed, setFailed] = useState(false);
  const [records, setRecords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sort, setSort] = useState("newest");
  const [detail, setDetail] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const search = useLiveSearch(client);
  useNoIndex();

  const load = useCallback(async () => {
    try {
      const [i, r, c] = await Promise.all([client.http.get("/portal/info").then((x) => x.data), client.records(), client.categories()]);
      setInfo(i); setRecords(r); setCategories(c);
    } catch { setFailed(true); }
  }, [client]);
  useEffect(() => { load(); }, [load]);

  const openRecord = async (id) => { try { setDetail(await client.record(id)); window.scrollTo({ top: 0 }); } catch { setDetail(null); } };
  const onMediaChanged = useCallback((m) => {
    setDetail((d) => (d ? { ...d, media: d.media.map((x) => (x.id === m.id ? { ...x, ...m } : x)) } : d));
    setViewer((v) => (v && v.id === m.id ? { ...v, ...m } : v));
  }, []);

  if (failed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f7fb] p-6" data-testid="portal-unavailable">
        <div className="text-center max-w-xs"><LinkBreak size={36} className="mx-auto text-slate-300 mb-2" /><div className="text-lg font-bold text-slate-800">Link unavailable</div><div className="text-sm text-slate-500 mt-1">This QR link is invalid, expired or has been revoked.</div></div>
      </div>
    );
  }
  if (!info) return <div className="min-h-screen bg-[#f5f7fb] p-6 text-sm text-slate-400">Loading…</div>;

  const visible = records.filter(search.isVisibleRecord).sort(SORTS[sort]);
  return (
    <div className="min-h-screen bg-[#f5f7fb]" data-testid="portal-page">
      <header className="bg-slate-900 text-white px-4 pt-5 pb-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Strata · Service Logs</div>
          <h1 className="text-2xl font-bold mt-1 flex items-start gap-1.5" data-testid="portal-address"><MapPin size={22} weight="fill" className="mt-1 text-orange-400 flex-shrink-0" />{info.property.full_address}</h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {detail ? (
          <PortalDetail detail={detail} onBack={() => setDetail(null)} onOpenMedia={setViewer} />
        ) : (
          <>
            <Button onClick={() => setFormOpen(true)} className="w-full h-12 text-base bg-orange-500 hover:bg-orange-600 text-white" data-testid="portal-new-entry-btn"><Plus size={18} weight="bold" className="mr-1.5" /> New Service Entry</Button>
            <div className="flex gap-2 items-start">
              <LiveSearchBar value={search.q} onChange={search.setQ} result={search.result} loading={search.loading} testId="portal-search"
                placeholder='Search — e.g. "boiler" or "10.12.26"' hideAddress onPick={(res) => openRecord(res.record.id)} />
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="portal-sort-select">
                <option value="newest">Newest</option><option value="oldest">Oldest</option><option value="title">Title</option><option value="category">Category</option><option value="unit">Unit</option>
              </select>
            </div>
            <div className="space-y-2" data-testid="portal-record-list">
              {visible.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="portal-empty">{search.active ? "No service logs match your search." : "No service logs yet."}</div>}
              {visible.map((r) => <RecordCard key={r.id} r={r} onOpen={(x) => openRecord(x.id)} />)}
            </div>
            <p className="text-[11px] text-slate-400 text-center pt-2">You can view logs and add new entries. Saved entries can't be edited here.</p>
          </>
        )}
      </main>
      <RecordModal
        open={formOpen}
        onOpenChange={setFormOpen}
        client={client}
        fixedProperty={info.property}
        properties={[]}
        categories={categories}
        onCategoryAdded={(c) => setCategories((l) => (l.some((x) => x.id === c.id) ? l : [...l, c]))}
        onSaved={(r, opts) => { load(); if (!opts?.keepOpen) openRecord(r.id); }}
      />
      <PhotoViewer media={viewer?.type === "photo" ? viewer : null} open={viewer?.type === "photo"} onOpenChange={(v) => !v && setViewer(null)} readOnly />
      <VideoViewer client={client} media={viewer?.type === "video" ? viewer : null} open={viewer?.type === "video"} onOpenChange={(v) => !v && setViewer(null)} onMediaChanged={onMediaChanged} readOnly />
    </div>
  );
}
